/** Staff check-in deep link with optional `account_id`. */
export function buildEventCheckInPath({ eventId, accountId } = {}) {
  const params = new URLSearchParams();
  const id = eventId != null ? String(eventId).trim() : "";
  const account = accountId != null ? String(accountId).trim() : "";
  if (id) params.set("eventId", id);
  if (account) params.set("account_id", account);
  const qs = params.toString();
  return qs ? `/EventQRCheckIn?${qs}` : "/EventQRCheckIn";
}

/** Build `/EventCalendar` with optional public `account_id` and deep-link `attendEventId`. */
export function buildEventCalendarPath({ accountId, attendEventId } = {}) {
  const params = new URLSearchParams();
  const id = accountId != null ? String(accountId).trim() : "";
  const eventId = attendEventId != null ? String(attendEventId).trim() : "";
  if (id) params.set("account_id", id);
  if (eventId) params.set("attendEventId", eventId);
  const qs = params.toString();
  return qs ? `/EventCalendar?${qs}` : "/EventCalendar";
}

/** Vanity marketing path `/e/{slug}` with optional `account_id` (needed off tenant subdomain). */
export function buildEventVanityPath({ slug, accountId } = {}) {
  const s = slug != null ? String(slug).trim().toLowerCase() : "";
  if (!s) return null;
  const params = new URLSearchParams();
  const id = accountId != null ? String(accountId).trim() : "";
  if (id) params.set("account_id", id);
  const qs = params.toString();
  return qs ? `/e/${encodeURIComponent(s)}?${qs}` : `/e/${encodeURIComponent(s)}`;
}

/**
 * Absolute marketing/registration URL for one event.
 * Prefers `/e/{slug}` when slug is set; otherwise EventCalendar + attendEventId.
 * @returns {string|null} null when origin, accountId, and (slug or attendEventId) are insufficient
 */
export function buildEventRegistrationUrl({
  accountId,
  attendEventId,
  slug,
  origin,
  includeAccountQuery = true,
} = {}) {
  const base = origin != null ? String(origin).trim().replace(/\/$/, "") : "";
  const id = accountId != null ? String(accountId).trim() : "";
  if (!base || !id) return null;
  const slugNorm = slug != null ? String(slug).trim().toLowerCase() : "";
  if (slugNorm) {
    const path = buildEventVanityPath({
      slug: slugNorm,
      accountId: includeAccountQuery ? id : "",
    });
    return path ? `${base}${path}` : null;
  }
  const eventId = attendEventId != null ? String(attendEventId).trim() : "";
  if (!eventId) return null;
  return `${base}${buildEventCalendarPath({ accountId: id, attendEventId: eventId })}`;
}

/** Post-login return path: keep vanity `/e/{slug}` when that is the landing URL. */
export function buildEventCalendarLoginReturnPath({
  eventSlug,
  accountId,
  attendEventId,
  includeAccountQuery,
} = {}) {
  const slug = eventSlug != null ? String(eventSlug).trim().toLowerCase() : "";
  const id = accountId != null ? String(accountId).trim() : "";
  if (slug) {
    return buildEventVanityPath({
      slug,
      accountId: includeAccountQuery && id ? id : "",
    });
  }
  const calendarAccountId = includeAccountQuery === false ? "" : id || undefined;
  return buildEventCalendarPath({ accountId: calendarAccountId, attendEventId });
}
